<script setup lang="ts">
import { computed, reactive } from "vue";
import { useStorage } from "@vueuse/core";

const DEBAG_FLAG = useStorage("debug-prerelease", false);

type StepperTab = {
  title: string;
  iconSuccess?: string | null;
  isValid?: boolean;
  sendCustomersCount?: number;
  isLoading?: boolean;
};

const emits = defineEmits<{
  (e: "update:step", step: number): void;
  (e: "finalize"): void;
  (e: "fake-finalize"): void;
}>();

const props = defineProps({
  step: {
    type: Number,
    default: 1,
  },
  tabs: {
    type: Array<StepperTab>,
    default: reactive([]),
  },
  backText: {
    type: String,
    default: "Назад",
  },
  nextText: {
    type: String,
    default: "Далее",
  },
  doneText: {
    type: String,
    default: "Готово",
  },
  finished: {
    type: Boolean,
    default: false,
  },
  primaryColor: {
    type: String,
    default: "#2db2e3",
  },
  secondaryColor: {
    type: String,
    default: "#fff",
  },
  circleSize: {
    type: Number,
    default: 32,
  },
  wrapperClass: {
    type: Array<string>,
    default: () => [],
  },
  contentClass: {
    type: Array<string>,
    default: () => [],
  },
});

const cssVars = computed(() => {
  return {
    "--primary-color": props.primaryColor,
    "--secondary-color": props.secondaryColor,
    "--circle-size": `${props.circleSize}px`,
  };
});

const incrementStep = () => {
  const step = props.step + 1;
  emits("update:step", step);
};

const decrementStep = () => {
  const step = props.step - 1;
  emits("update:step", step);
};

const progress = computed(() => {
  return (100 / (props.tabs.length - 1)) * (props.step - 1) + "%";
});

const circleStyles = computed(() => {
  return {
    height: `${props.circleSize}px`,
    width: `${props.circleSize}px`,
  };
});
</script>

<template>
  <div class="wrapper" :style="cssVars" :class="wrapperClass">
    <div class="stepper">
      <div class="stepper__progress">
        <div class="stepper__progress-bar" :style="'width:' + progress"></div>
      </div>

      <div
        class="stepper__item"
        :class="{
          current: props.step === index + 1,
          success: props.step > index + 1,
        }"
        v-for="(item, index) in props.tabs"
        :key="index"
      >
        <div class="stepper__item-counter" :style="circleStyles">
          <img
            v-if="item.iconSuccess"
            class="icon-success"
            :src="item.iconSuccess"
            alt="Check Mark"
          />
          <svg
            v-else
            class="icon-success"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            :style="{ fill: secondaryColor }"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M18.1897 4.77586C18.5896 5.15675 18.6051 5.78973 18.2242 6.18966L9.02923 15.8444C8.18307 16.7328 6.74378 16.657 5.9957 15.6845L1.70743 10.1097C1.37069 9.67196 1.45258 9.04411 1.89034 8.70738C2.32809 8.37064 2.95594 8.45254 3.29267 8.89029L7.58095 14.4651L16.7759 4.81035C17.1568 4.41042 17.7898 4.39498 18.1897 4.77586Z"
            />
          </svg>
          <span class="number">{{ index + 1 }}</span>
        </div>
        <span class="stepper__item-title">{{ item.title }}</span>
      </div>
    </div>

    <template v-for="index in props.tabs.length" :key="index">
      <div
        class="stepper__content"
        v-if="props.step === index"
        :class="contentClass"
      >
        <div class="stepper__pane">
          <slot :name="index"></slot>
        </div>
      </div>
    </template>

    <div class="stepper__navs">
      <button
        class="btn btn--back"
        type="button"
        @click="decrementStep"
        v-if="props.step !== 1"
      >
        {{ props.backText }}
      </button>
      <button
        class="btn btn--default-2"
        type="button"
        @click="incrementStep"
        v-if="props.step !== props.tabs.length"
        :disabled="!props.tabs[props.step - 1].isValid"
      >
        {{ props.nextText }}
      </button>
      <button
        class="btn btn--default-2"
        type="button"
        @click="emits('finalize')"
        v-else
        :disabled="!props.tabs[props.step - 1].isValid || finished"
      >
        <span v-if="props.tabs[props.step - 1].isLoading" class="loader"></span>
        <span v-else>{{ props.doneText }}</span>
      </button>
      <button
        class="btn btn--default-2"
        type="button"
        @click="emits('fake-finalize')"
        v-if="props.step === props.tabs.length && DEBAG_FLAG"
      >
        Провести нагрузочный тест
      </button>
    </div>
  </div>
</template>

<style scoped>
.wrapper {
  --black-color: #2b3238;
  --grey-color: #b6c7d6;
  --white-color: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  gap: 32px;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: auto;
  position: relative;
  z-index: 0;
  margin: 0px 28px;
  box-sizing: border-box;
}

.stepper__progress {
  position: absolute;
  background-color: var(--grey-color);
  height: 2px;
  z-index: -1;
  left: 0;
  right: 0;
  margin: 0 auto;
}

.stepper__progress-bar {
  position: absolute;
  left: 0;
  height: 100%;
  width: 0;
  background-color: var(--primary-color);
  transition: all 500ms ease;
}

.stepper__item {
  box-sizing: border-box;
  padding-left: 12px;
  display: flex;
  align-items: center;
  transition: all 500ms ease;
  background-color: var(--white-color);
}

.stepper__item-counter {
  height: 32px;
  width: 32px;
  display: grid;
  place-items: center;
  background-color: var(--secondaryColor);
  border-radius: 100%;
  border: 2px solid var(--grey-color);
  position: relative;
  color: var(--grey-color);
}

.stepper__item-counter .icon-success {
  position: absolute;
  opacity: 0;
  transform: scale(0);
  width: 20px;
  transition: all 500ms ease;
}

.stepper__item-counter .number {
  font-size: 16px;
  font-weight: 700;
  transition: all 500ms ease;
}

.stepper__item-title {
  font-size: 14px;
  background-color: var(--white-color);
  color: var(--grey-color);
  box-sizing: border-box;
  padding: 0px 12px;
  font-weight: 600;
  line-height: 20px;
}

.stepper__item.success .stepper__item-counter {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  font-weight: 600;
  height: var(--circleSize);
  width: var(--circleSize);

  .icon-success {
    opacity: 1;
    transform: scale(1);
  }

  .number {
    opacity: 0;
    transform: scale(0);
  }
}

.stepper__item.success .stepper__item-title {
  color: var(--black-color);
}

.stepper__item.current .stepper__item-counter {
  border-color: var(--primary-color);
  background-color: var(--primary-color);
  color: var(--white-color);
  font-weight: 600;
}

.stepper__item.current .stepper__item-title {
  color: var(--black-color);
}

.stepper__navs {
  display: flex;
  box-sizing: border-box;
  padding: 16px 40px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.08) inset;
  justify-content: flex-end;
  gap: 12px;
}

.stepper__content {
  flex-grow: 1;
  box-sizing: border-box;
  padding: 0px 40px;
  overflow: hidden;
}

.stepper__pane {
  height: 100%;
}

.btn {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 150ms;
  border-radius: 8px;
  box-sizing: border-box;
  padding: 12px 0px;
  width: 196px;
  color: var(--black-color);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.btn--back {
  color: var(--black-color);
  background-color: #f1f5f9;
}

.btn--default-2 {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: var(--white-color);
}

.btn:disabled {
  background-color: #e5ebf0;
  color: var(--grey-color);
  pointer-events: none;
}

.loader {
  width: 20px;
  height: 20px;
  border: 2px solid var(--white-color);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
